/**
 * DOCUMENTATION:
 * Component: localRAG.js
 * Purpose: Local Vector Search Engine (RAG).
 *
 * The student's live question is the one thing that can't be
 * precomputed, so it still needs to be turned into a vector
 * (embedded) at search time. That embedding call goes through the
 * Firebase Cloud Function (functions/aiMentor.js) via the aiMentor
 * callable — the Gemini key lives in Firebase Secret Manager,
 * never in this file or the browser.
 *
 * Everything else (the curriculum's own vectors) is precomputed
 * ahead of time by scripts/precomputeEmbeddings.mjs and shipped as
 * precomputedVectors.json, then cached locally in IndexedDB so
 * repeat visits don't need to reload it.
 */

import { httpsCallable } from "firebase/functions";
import { MemoryVectorStore } from "@langchain/classic/vectorstores/memory";
import { functions } from "../firebase";
import precomputedVectors from "./precomputedVectors.json";

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────

const SEARCH_MAX_RETRIES = 3;
const FALLBACK_WAIT_MS   = 45000;

// IndexedDB settings
const IDB_NAME  = "ProDevRAG";
const IDB_STORE = "vectorCache";
const IDB_KEY   = "prodev_rag_index_v3";

// ─────────────────────────────────────────────────────────────
// EMBEDDINGS — a thin client for the aiMentor Cloud Function
// ─────────────────────────────────────────────────────────────

// Callable reference to the Firebase Cloud Function. Firebase attaches
// the signed-in user's auth token automatically — no fetch(), no URL,
// no key anywhere near this file.
const aiMentor = httpsCallable(functions, "aiMentor");

/**
 * A minimal object matching the shape MemoryVectorStore expects
 * (an `embedQuery` method). It never touches a Gemini key directly —
 * it just calls the aiMentor Cloud Function.
 */
const embeddings = {
    embedQuery: async (text) => {
        const result = await aiMentor({ action: "embed", text });
        return result.data?.embedding;
    },
    // Document embedding happens offline now, via scripts/precomputeEmbeddings.mjs —
    // this should never be called from the running app.
    embedDocuments: async () => {
        throw new Error(
            "embedDocuments() should not run in the browser. Use scripts/precomputeEmbeddings.mjs instead."
        );
    },
};

// ─────────────────────────────────────────────────────────────
// SINGLETON STATE
// ─────────────────────────────────────────────────────────────

let vectorStore  = null;
let isIndexing   = false;
let indexPromise = null;

// ─────────────────────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────────────────────

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const isRateLimitError = (err) =>
    err?.status === 429 ||
    err?.message?.includes("429") ||
    err?.message?.toLowerCase().includes("too many requests") ||
    err?.message?.toLowerCase().includes("quota");

const parseRetryDelay = (err) => {
    try {
        const match = err?.message?.match(/retry in ([\d.]+)s/i);
        if (match) return Math.ceil(parseFloat(match[1]) * 1000) + 1000;
    } catch { /* ignore */ }
    return FALLBACK_WAIT_MS;
};

// ─────────────────────────────────────────────────────────────
// INDEXEDDB CACHE
// ─────────────────────────────────────────────────────────────

const openDB = () =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, 1);
        req.onupgradeneeded = (e) => e.target.result.createObjectStore(IDB_STORE);
        req.onsuccess       = (e) => resolve(e.target.result);
        req.onerror         = (e) => reject(e.target.error);
    });

const saveToIDB = async (vectors) => {
    try {
        const db = await openDB();
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).put(vectors, IDB_KEY);
        await new Promise((res, rej) => {
            tx.oncomplete = res;
            tx.onerror    = () => rej(tx.error);
        });
        db.close();
        console.log("RAG: Index cached in IndexedDB.");
    } catch (e) {
        console.warn("RAG: Could not write IndexedDB cache:", e.message);
    }
};

const loadFromIDB = async () => {
    try {
        const db      = await openDB();
        const tx      = db.transaction(IDB_STORE, "readonly");
        const vectors = await new Promise((res, rej) => {
            const req     = tx.objectStore(IDB_STORE).get(IDB_KEY);
            req.onsuccess = () => res(req.result);
            req.onerror   = () => rej(req.error);
        });
        db.close();

        if (!Array.isArray(vectors) || vectors.length === 0) return null;

        const store = new MemoryVectorStore(embeddings);
        store.memoryVectors = vectors;
        return store;
    } catch {
        return null;
    }
};

const deleteFromIDB = async () => {
    try {
        const db = await openDB();
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.objectStore(IDB_STORE).delete(IDB_KEY);
        db.close();
    } catch { /* ignore */ }
};

// ─────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────

export const initializeSearch = async () => {
    if (vectorStore) return;
    if (isIndexing)  return indexPromise;

    isIndexing = true;

    indexPromise = (async () => {
        try {
            console.log("System: Constructing ProDev Memory Index…");

            const cached = await loadFromIDB();
            if (cached) {
                vectorStore = cached;
                console.log("✅ ProDev Search: Restored from IndexedDB cache. Ready.");
                return;
            }

            const store = new MemoryVectorStore(embeddings);
            store.memoryVectors = precomputedVectors;

            await saveToIDB(store.memoryVectors);

            vectorStore = store;
            console.log(
                `✅ ProDev Search: Indexed and ready from static build (${precomputedVectors.length} docs).`
            );

        } catch (error) {
            console.error("❌ RAG Init Error:", error);
        } finally {
            isIndexing = false;
        }
    })();

    return indexPromise;
};

/**
 * Search the curriculum for the top-k relevant results.
 * The one live call this makes (embedding the user's question) now
 * goes through the aiMentor Cloud Function, not a client-side Gemini key.
 */
export const searchLessons = async (query, k = 3) => {
    if (!vectorStore) await initializeSearch();

    let attempt = 0;

    while (attempt < SEARCH_MAX_RETRIES) {
        try {
            return await vectorStore.similaritySearch(query, k);
        } catch (e) {
            attempt++;

            if (isRateLimitError(e) && attempt < SEARCH_MAX_RETRIES) {
                const waitMs = parseRetryDelay(e);
                console.warn(`RAG Search: Rate-limited (attempt ${attempt}/${SEARCH_MAX_RETRIES}). Waiting ${Math.ceil(waitMs / 1000)}s…`);
                await sleep(waitMs);
                continue;
            }

            console.error("❌ Vector Search Error:", e);
            return [];
        }
    }

    return [];
};

export const clearRAGCache = async () => {
    vectorStore  = null;
    isIndexing   = false;
    indexPromise = null;
    await deleteFromIDB();
    console.log("RAG: Cache cleared — next call will reload from the static build.");
};
